import { useAuth0 } from "@auth0/auth0-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useState } from "react";
import {
  deleteMedia,
  getMediaUrl,
  listPersonMedia,
  MediaItem,
  updateMedia,
  uploadMedia,
} from "../../api/trees";
import { useVaultContext } from "../../context/VaultContext";
import LoadingSpinner from "../Common/LoadingSpinner";

interface MediaGalleryProps {
  treeId: number;
  personId: number;
}

export default function MediaGallery({ treeId, personId }: MediaGalleryProps) {
  const { getAccessTokenSilently } = useAuth0();
  const queryClient = useQueryClient();
  const { currentVaultId } = useVaultContext();
  const [selectedMedia, setSelectedMedia] = useState<MediaItem | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  const { data: media, isLoading } = useQuery({
    queryKey: ["media", personId],
    queryFn: async () => {
      const token = await getAccessTokenSilently();
      return listPersonMedia(personId, token);
    },
  });

  const uploadMutation = useMutation({
    mutationFn: async ({
      file,
      isProfile,
    }: {
      file: File;
      isProfile?: boolean;
    }) => {
      const token = await getAccessTokenSilently();
      return uploadMedia(file, treeId, token, {
        vaultId: currentVaultId ?? undefined,
        personId,
        isProfile,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["media", personId] });
      queryClient.invalidateQueries({ queryKey: ["person", String(treeId), String(personId)] });
      setIsUploading(false);
      setUploadError(null);
    },
    onError: (err: Error) => {
      setUploadError(err.message);
      setIsUploading(false);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (mediaId: number) => {
      const token = await getAccessTokenSilently();
      return deleteMedia(mediaId, token);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["media", personId] });
      queryClient.invalidateQueries({ queryKey: ["person", String(treeId), String(personId)] });
      setSelectedMedia(null);
    },
  });

  const setProfileMutation = useMutation({
    mutationFn: async (mediaId: number) => {
      const token = await getAccessTokenSilently();
      return updateMedia(mediaId, { is_profile: true }, token);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["media", personId] });
      queryClient.invalidateQueries({ queryKey: ["person", String(treeId), String(personId)] });
    },
  });

  const handleFileSelect = useCallback(
    async (files: FileList | null) => {
      if (!files || files.length === 0) return;

      setIsUploading(true);
      setUploadError(null);

      // Check if this is the first photo (make it profile by default)
      const isFirstPhoto = !media || media.length === 0;

      for (const file of Array.from(files)) {
        try {
          await uploadMutation.mutateAsync({
            file,
            isProfile: isFirstPhoto && file === files[0],
          });
        } catch {
          // Error already handled by mutation
        }
      }
    },
    [media, uploadMutation]
  );

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragging(false);
      handleFileSelect(e.dataTransfer.files);
    },
    [handleFileSelect]
  );

  const handleDelete = (mediaItem: MediaItem) => {
    if (window.confirm(`Delete "${mediaItem.original_filename}"?`)) {
      deleteMutation.mutate(mediaItem.id);
    }
  };

  const handleSetProfile = (mediaItem: MediaItem) => {
    setProfileMutation.mutate(mediaItem.id);
  };

  const profilePhoto = media?.find((m) => m.is_profile);
  const otherMedia = media?.filter((m) => !m.is_profile) || [];

  return (
    <div className="rounded-lg bg-white p-6 shadow-sm">
      <h2 className="font-serif text-xl font-semibold text-vault-900">Photos & Documents</h2>

      {/* Upload Area */}
      <div
        className={`mt-4 rounded-lg border-2 border-dashed p-4 text-center transition-colors ${
          isDragging
            ? "border-vault-500 bg-vault-50"
            : "border-vault-300 hover:border-vault-400"
        }`}
        onDrop={handleDrop}
        onDragOver={(e) => {
          e.preventDefault();
          setIsDragging(true);
        }}
        onDragLeave={(e) => {
          e.preventDefault();
          setIsDragging(false);
        }}
      >
        {isUploading ? (
          <div className="py-2">
            <LoadingSpinner size="sm" />
            <p className="mt-2 text-sm text-vault-600">Uploading...</p>
          </div>
        ) : (
          <>
            <p className="text-sm text-vault-600">
              Drag photos here or{" "}
              <label className="cursor-pointer text-vault-700 underline hover:text-vault-900">
                browse
                <input
                  type="file"
                  accept="image/*,.pdf,.doc,.docx"
                  multiple
                  onChange={(e) => handleFileSelect(e.target.files)}
                  className="hidden"
                />
              </label>
            </p>
          </>
        )}
      </div>

      {uploadError && (
        <div className="mt-2 rounded-lg bg-red-50 p-3">
          <p className="text-sm font-medium text-red-800">Upload failed</p>
          <p className="mt-1 text-sm text-red-600">{uploadError}</p>
          <button
            onClick={() => setUploadError(null)}
            className="mt-2 text-xs text-red-700 underline hover:text-red-900"
          >
            Dismiss
          </button>
        </div>
      )}

      {/* Loading */}
      {isLoading && (
        <div className="mt-4 flex justify-center py-4">
          <LoadingSpinner />
        </div>
      )}

      {/* Gallery */}
      {!isLoading && media && media.length > 0 && (
        <div className="mt-4">
          {/* Profile Photo */}
          {profilePhoto && (
            <div className="mb-4">
              <p className="text-xs font-medium uppercase text-vault-500">Profile Photo</p>
              <div className="mt-2 flex items-start gap-4">
                <MediaThumbnail
                  media={profilePhoto}
                  onClick={() => setSelectedMedia(profilePhoto)}
                  size="lg"
                />
                <div className="text-sm text-vault-600">
                  <p className="font-medium">{profilePhoto.original_filename}</p>
                  <p className="text-xs text-vault-400">
                    {formatFileSize(profilePhoto.file_size)}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Other Media */}
          {otherMedia.length > 0 && (
            <div>
              <p className="text-xs font-medium uppercase text-vault-500">
                {profilePhoto ? "Other Media" : "Media"}
              </p>
              <div className="mt-2 grid grid-cols-4 gap-2">
                {otherMedia.map((item) => (
                  <MediaThumbnail
                    key={item.id}
                    media={item}
                    onClick={() => setSelectedMedia(item)}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Empty State */}
      {!isLoading && (!media || media.length === 0) && (
        <p className="mt-4 text-sm text-vault-500">No photos or documents yet.</p>
      )}

      {/* Media Detail Modal */}
      {selectedMedia && (
        <MediaModal
          media={selectedMedia}
          onClose={() => setSelectedMedia(null)}
          onDelete={() => handleDelete(selectedMedia)}
          onSetProfile={() => handleSetProfile(selectedMedia)}
          isDeleting={deleteMutation.isPending}
        />
      )}
    </div>
  );
}

function MediaThumbnail({
  media,
  onClick,
  size = "sm",
}: {
  media: MediaItem;
  onClick: () => void;
  size?: "sm" | "lg";
}) {
  const { getAccessTokenSilently } = useAuth0();
  const [imageUrl, setImageUrl] = useState<string | null>(null);

  // Fetch image with auth token
  useState(() => {
    if (media.media_type === "photo") {
      getAccessTokenSilently().then((token) => {
        fetch(getMediaUrl(media.id), {
          headers: { Authorization: `Bearer ${token}` },
        })
          .then((res) => res.blob())
          .then((blob) => setImageUrl(URL.createObjectURL(blob)));
      });
    }
  });

  const sizeClasses = size === "lg" ? "h-24 w-24" : "h-16 w-16";

  return (
    <button
      onClick={onClick}
      className={`${sizeClasses} flex-shrink-0 overflow-hidden rounded-lg bg-vault-100 transition-opacity hover:opacity-80`}
    >
      {media.media_type === "photo" && imageUrl ? (
        <img
          src={imageUrl}
          alt={media.caption || media.original_filename}
          className="h-full w-full object-cover"
        />
      ) : (
        <div className="flex h-full w-full items-center justify-center text-vault-400">
          {media.media_type === "document" ? (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={1.5}
              stroke="currentColor"
              className="h-6 w-6"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z"
              />
            </svg>
          ) : (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={1.5}
              stroke="currentColor"
              className="h-6 w-6"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909m-18 3.75h16.5a1.5 1.5 0 001.5-1.5V6a1.5 1.5 0 00-1.5-1.5H3.75A1.5 1.5 0 002.25 6v12a1.5 1.5 0 001.5 1.5zm10.5-11.25h.008v.008h-.008V8.25zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z"
              />
            </svg>
          )}
        </div>
      )}
    </button>
  );
}

function MediaModal({
  media,
  onClose,
  onDelete,
  onSetProfile,
  isDeleting,
}: {
  media: MediaItem;
  onClose: () => void;
  onDelete: () => void;
  onSetProfile: () => void;
  isDeleting: boolean;
}) {
  const { getAccessTokenSilently } = useAuth0();
  const [imageUrl, setImageUrl] = useState<string | null>(null);

  // Fetch image with auth token
  useState(() => {
    if (media.media_type === "photo") {
      getAccessTokenSilently().then((token) => {
        fetch(getMediaUrl(media.id), {
          headers: { Authorization: `Bearer ${token}` },
        })
          .then((res) => res.blob())
          .then((blob) => setImageUrl(URL.createObjectURL(blob)));
      });
    }
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/70" onClick={onClose} />
      <div className="relative max-h-[90vh] max-w-4xl overflow-auto rounded-lg bg-white p-4">
        <button
          onClick={onClose}
          className="absolute right-2 top-2 rounded-full bg-white/80 p-1 text-vault-600 hover:bg-white"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={1.5}
            stroke="currentColor"
            className="h-6 w-6"
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        {/* Image */}
        {media.media_type === "photo" && imageUrl && (
          <img
            src={imageUrl}
            alt={media.caption || media.original_filename}
            className="max-h-[60vh] w-auto rounded"
          />
        )}

        {/* Details */}
        <div className="mt-4">
          <h3 className="font-medium text-vault-900">{media.original_filename}</h3>
          {media.caption && <p className="text-sm text-vault-600">{media.caption}</p>}
          <p className="mt-1 text-xs text-vault-400">
            {formatFileSize(media.file_size)} • Uploaded{" "}
            {new Date(media.created_at).toLocaleDateString()}
          </p>
        </div>

        {/* Actions */}
        <div className="mt-4 flex gap-2">
          {!media.is_profile && media.media_type === "photo" && (
            <button
              onClick={onSetProfile}
              className="rounded-lg bg-vault-100 px-3 py-1.5 text-sm text-vault-700 hover:bg-vault-200"
            >
              Set as Profile Photo
            </button>
          )}
          <button
            onClick={onDelete}
            disabled={isDeleting}
            className="rounded-lg bg-red-50 px-3 py-1.5 text-sm text-red-600 hover:bg-red-100"
          >
            {isDeleting ? "Deleting..." : "Delete"}
          </button>
        </div>
      </div>
    </div>
  );
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

